'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';

// Sanitize string to remove invalid characters that cause DOMException in some browsers
function sanitizeForDOM(str: string): string {
  if (!str) return '';
  try {
    // Remove null bytes, control characters, and other problematic Unicode characters
    // eslint-disable-next-line no-control-regex
    return str.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '').replace(/[\x80-\x9F]/g, '').replace(/\uFFFE|\uFFFF/g, '');
  } catch {
    return '';
  }
}
interface VideoEmbedProps {
  embedJs: string;
  type?: string;
  subtype?: string;
}

// Skeleton component for video loading state
const VideoSkeleton = () => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      paddingTop: "56.25%"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="relative w-full bg-gray-200 rounded-lg overflow-hidden" style={t0}><div className="absolute inset-0 flex items-center justify-center"><div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center"><div className="w-0 h-0 border-t-8 border-t-transparent border-l-12 border-l-gray-400 border-b-8 border-b-transparent ml-1" style={{
            borderLeftWidth: "12px"
          }} /></div></div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="mb-4 animate-pulse">{t1}<div className="mt-3 space-y-2"><div className="h-4 bg-gray-200 rounded w-3/4" /><div className="h-3 bg-gray-200 rounded w-1/2" /></div></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
export default function VideoEmbed({
  embedJs,
  type,
  subtype
}: VideoEmbedProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    if (containerRef.current) {
      try {
        // Decode the embed content - handle both Base64 and plain HTML
        let decodedContent: string;
        try {
          // Try Base64 decoding first
          decodedContent = atob(embedJs);
        } catch {
          // If atob fails, the content might be plain HTML or URL-encoded
          try {
            decodedContent = decodeURIComponent(embedJs);
          } catch {
            // Use as-is if all decoding fails
            decodedContent = embedJs;
          }
        }
        // Sanitize to remove invalid characters before setting innerHTML
        const sanitizedContent = sanitizeForDOM(decodedContent);

        // Parse the content to extract scripts
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = sanitizedContent;

        // Get all script elements before setting innerHTML (since they won't execute via innerHTML)
        const scriptElements = tempDiv.querySelectorAll('script');
        const scriptsToExecute: {
          src?: string;
          content?: string;
          charset?: string;
        }[] = [];
        scriptElements.forEach(script => {
          if (script.src) {
            scriptsToExecute.push({
              src: script.src,
              charset: script.charset || 'utf-8'
            });
          } else if (script.textContent) {
            scriptsToExecute.push({
              content: script.textContent
            });
          }
          script.remove();
        });

        // Set HTML content without scripts
        containerRef.current.innerHTML = tempDiv.innerHTML;

        // Execute scripts
        if (scriptsToExecute.length > 0) {
          let scriptsLoaded = 0;
          const totalScripts = scriptsToExecute.filter(s => s.src).length;
          const checkAllLoaded = () => {
            scriptsLoaded++;
            if (scriptsLoaded >= totalScripts || totalScripts === 0) {
              // Give extra time for video to render
              setTimeout(() => setIsLoading(false), 800);
            }
          };
          scriptsToExecute.forEach(scriptInfo => {
            if (scriptInfo.src) {
              // External script - create new script element to load it
              const newScript = document.createElement('script');
              newScript.src = scriptInfo.src;
              newScript.async = true;
              if (scriptInfo.charset) newScript.charset = scriptInfo.charset;
              newScript.onload = () => {
                checkAllLoaded();
              };
              newScript.onerror = () => {
                console.error('VideoEmbed: Failed to load external script', scriptInfo.src);
                checkAllLoaded();
              };
              document.head.appendChild(newScript);
            } else if (scriptInfo.content) {
              // Inline script - execute it
              try {
                const fn = new Function(scriptInfo.content);
                fn();
              } catch (err_0) {
                console.error('VideoEmbed: Error executing inline script', err_0);
              }
            }
          });

          // If no external scripts, just wait a bit
          if (totalScripts === 0) {
            setTimeout(() => setIsLoading(false), 500);
          }
        } else {
          // No scripts, hide skeleton after a delay
          setTimeout(() => setIsLoading(false), 300);
        }

        // Observe for iframe/video element appearing
        const observer = new MutationObserver(() => {
          const hasMedia = containerRef.current?.querySelector('iframe, video');
          if (hasMedia) {
            setTimeout(() => setIsLoading(false), 300);
            observer.disconnect();
          }
        });
        observer.observe(containerRef.current, {
          childList: true,
          subtree: true
        });

        // Fallback timeout
        const fallbackTimer = setTimeout(() => {
          setIsLoading(false);
          observer.disconnect();
        }, 10000);
        return () => {
          observer.disconnect();
          clearTimeout(fallbackTimer);
        };
      } catch (err) {
        console.error('VideoEmbed: Error processing embed', err);
        setError('Failed to process video embed');
        setIsLoading(false);
      }
    }
  }, [embedJs, type, subtype]);
  if (error) {
    return <div className="mb-4 p-4 border border-red-300 bg-red-50 rounded">
        <p className="text-red-600">Error loading video: {error}</p>
      </div>;
  }
  return <div className="relative">
      {isLoading && <VideoSkeleton />}
      <div ref={containerRef} className={`mb-4 ${isLoading ? 'absolute opacity-0 pointer-events-none' : ''}`} />
    </div>;
}